import React, { useId, useState } from 'react';
import { WoodHatchPattern } from './DraftingPrimitives';

/**
 * PerspectiveViewer — modern-clean scalable drawing viewer.
 *
 * Replaces the heavy editorial ProductPlate for the new interface.
 * ---------------------------------------------------------------------------
 *  – Zeichnungen wachsen proportional zu den echten Maßen (mm).
 *  – Drei umschaltbare 2D-Ansichten pro Produkt: Draufsicht / Einbau / Iso.
 *  – „Im Dach“ ist der Längsschnitt von der Seite.
 *  – Beim ACCESS zwei Schnitte aus der Herstellerzeichnung: Scharnierseite
 *    (kurze Seite) und Motorseite (lange Seite).
 *  – Bemaßungen zeigen die realen Bestellmaße; Rohbau + 30 mm bleibt sichtbar.
 *  – Keine DIN-Cartouche, keine ALL-CAPS-Schilder — das ganze Chrome ist
 *    modern-ruhig, die Zeichnung selbst darf technisch bleiben.
 */

export type ProductKind = 'fixed' | 'comfort' | 'circular' | 'linear' | 'access';
export type ViewName = 'photo' | 'top' | 'section' | 'iso';

export interface PerspectiveDimensions {
  /** Bestellmaß Breite (mm). Bei LINEAR: Länge des Lichtbands. */
  width: number;
  /** Bestellmaß Länge (mm). Bei LINEAR: kurze Seite quer zum Band. */
  length: number;
  /** Aufbauhöhe (mm) — für Schnittansicht. */
  height?: number;
  /** Durchmesser (mm) — nur CIRCULAR. */
  diameter?: number;
  /** Anzahl Module (nur LINEAR). */
  modules?: number;
  /** Davon öffenbar (nur LINEAR). Die ersten Felder im Band. */
  openingModules?: number;
}

interface Props {
  kind: ProductKind;
  dimensions: PerspectiveDimensions;
  /** rubric-Highlight, wenn Produkt ausgewählt ist. */
  selected?: boolean;
  /** Initialansicht, wenn keine controlled `view` gesetzt ist. */
  initialView?: ViewName;
  /** Controlled view. */
  view?: ViewName;
  onViewChange?: (v: ViewName) => void;
  /** Bemaßungen unterdrücken (für sehr kleine Miniaturansichten). */
  hideDimensions?: boolean;
  /** Toggle unter der Zeichnung anzeigen? Default: true. */
  showToggle?: boolean;
  /** Interner Größenmodifikator für die Toggle-Größe. */
  size?: 'sm' | 'md' | 'lg';
  /** Produktfoto. Leere Plugin-Platzhalter werden ignoriert. */
  photoUrl?: string;
  /** Iso-Rendering auf Weiß. Fallback, wenn das Dachfoto fehlt. */
  renderUrl?: string;
  /** Produkt-Id oder -Name, damit WALKON/ACCESS das eigene Foto bekommen. */
  productKey?: string;
  /** Rohbauöffnung (+30 mm) in der Draufsicht mitzeichnen. */
  showOpening?: boolean;
}

/** Plugin-Asset-Basis — WP injiziert `plugin_url`, Preview fällt auf `../` zurück. */
export function assetBase(): string {
  const injected =
    typeof window !== 'undefined'
      ? ((window as unknown as { skyvision_ajax?: { plugin_url?: string } }).skyvision_ajax
          ?.plugin_url as string | undefined)
      : undefined;
  if (injected) return injected.endsWith('/') ? injected : `${injected}/`;
  return '../';
}

/** Cache-Buster für Produktfotos. Anheben, wenn assets/photos ausgetauscht werden. */
const PHOTO_CACHE = '14';

const ISO_JPEG = new Set([
  'skyvision-fixed',
  'skyvision-comfort',
  'skyvision-circular',
  'skyvision-access',
  'skyvision-walkon',
  'skyvision-linear',
  'skyvision-linear-walkon',
  'skyvision-circular-walkon',
]);

/** Dateiname ohne Endung: skyvision-fixed, skyvision-linear-walkon, … */
export function productAssetStem(kind: ProductKind, hint?: string): string {
  const h = (hint || '').toLowerCase();
  if (h.includes('linear') && (h.includes('walkon') || h.includes('walk-on'))) {
    return 'skyvision-linear-walkon';
  }
  if (h.includes('circular') && (h.includes('walkon') || h.includes('walk-on'))) {
    return 'skyvision-circular-walkon';
  }
  if (h.includes('walkon') || h.includes('walk-on')) return 'skyvision-walkon';
  if (h.includes('circular')) return 'skyvision-circular';
  if (h.includes('linear')) return 'skyvision-linear';
  if (h.includes('access')) return 'skyvision-access';
  if (h.includes('comfort')) return 'skyvision-comfort';
  if (h.includes('fixed')) return 'skyvision-fixed';
  switch (kind) {
    case 'comfort':
      return 'skyvision-comfort';
    case 'circular':
      return 'skyvision-circular';
    case 'linear':
      return 'skyvision-linear';
    case 'access':
      return 'skyvision-access';
    default:
      return 'skyvision-fixed';
  }
}

export function productImageUrls(
  kind: ProductKind,
  hint?: string,
): { photoUrl: string; renderUrl: string } {
  const base = assetBase();
  const id = productAssetStem(kind, hint);
  const isoExt = ISO_JPEG.has(id) ? 'jpg' : 'png';
  return {
    photoUrl: `${base}assets/photos/${id}.jpg?v=${PHOTO_CACHE}`,
    renderUrl: `${base}assets/iso/${id}.${isoExt}?v=10`,
  };
}

export function explainImageUrls(): {
  schnitt: string;
  innen: string;
  draufsicht: string;
} {
  const base = assetBase();
  return {
    schnitt: `${base}assets/explain/rohbaulichte-schnitt.jpg?v=5`,
    innen: `${base}assets/explain/rohbaulichte-innen.jpg?v=4`,
    draufsicht: `${base}assets/explain/rohbaulichte-draufsicht.jpg?v=4`,
  };
}

/** Admin-Platzhalter unter assets/images/, die Datei existiert oft nicht. */
function isPlaceholderPhoto(url?: string): boolean {
  if (!url) return true;
  return /\/assets\/images\/skyvision-[a-z-]+\.jpe?g(\?|$)/i.test(url);
}

/** Hängt die Foto-Version an Plugin-URLs, damit ausgetauschte JPEGs nicht im Cache bleiben. */
function withPhotoCache(url?: string): string | undefined {
  if (!url) return url;
  if (!/\/assets\/photos\/skyvision-[a-z0-9-]+\.jpe?g/i.test(url)) return url;
  if (/[?&]v=\d+/.test(url)) return url.replace(/([?&]v=)\d+/, `$1${PHOTO_CACHE}`);
  return `${url}${url.includes('?') ? '&' : '?'}v=${PHOTO_CACHE}`;
}

export function productKindFromName(name: string): ProductKind {
  const n = name.toLowerCase();
  if (n.includes('circular')) return 'circular';
  if (n.includes('linear')) return 'linear';
  if (n.includes('access')) return 'access';
  if (n.includes('comfort')) return 'comfort';
  return 'fixed';
}

/**
 * Mappt den Konfigurator-State auf die Viewer-Konvention für LINEAR:
 * width = Bandlänge, length = kurze Seite.
 */
export function linearViewerDimensions(
  dimensions: { width: number; length: number; height?: number },
  modules: { fixed?: number; opening?: number },
): PerspectiveDimensions {
  const opening = modules.opening || 0;
  const count = Math.max(1, (modules.fixed || 0) + opening);
  return {
    width: dimensions.length || count * 1000,
    length: dimensions.width || 1000,
    height: dimensions.height,
    modules: count,
    openingModules: opening,
  };
}

function linearSpan(d: PerspectiveDimensions) {
  const modules = Math.max(1, d.modules ?? 3);
  const opening = Math.max(0, Math.min(modules, d.openingModules ?? 0));
  const bandMm = Math.max(300, d.modules ? d.width || modules * 1000 : modules * 1000);
  const shortMm = Math.max(300, d.length ?? 1000);
  return { modules, opening, bandMm, shortMm, pitchMm: bandMm / modules };
}

const INK = '#3A3C3E';
const RUB = '#77B72B';
const MUTED = '#4A4E55';
const PAPER2 = '#F3F5F4';
const GLASS = '#D8E4EE';
const ROOF = '#E6EAE7';

/* --- Public helper ------------------------------------------------------- */

/** Formatiert einen mm-Wert als deutsche Zahl. */
function fmt(n: number): string {
  return Math.round(n).toLocaleString('de-DE');
}

/* ------------------------------------------------------------------------ */

export default function PerspectiveViewer({
  kind,
  dimensions,
  selected = false,
  initialView = 'photo',
  view: controlledView,
  onViewChange,
  hideDimensions = false,
  showToggle = true,
  size = 'md',
  photoUrl,
  renderUrl,
  productKey,
  showOpening = false,
}: Props) {
  const [uncontrolled, setUncontrolled] = useState<ViewName>(initialView);
  const [accessCut, setAccessCut] = useState<'hinge' | 'motor'>('hinge');
  const view = controlledView ?? uncontrolled;

  const setView = (v: ViewName) => {
    if (onViewChange) onViewChange(v);
    else setUncontrolled(v);
  };

  const color = selected ? RUB : INK;
  const images = productImageUrls(kind, productKey);
  const resolvedRender = renderUrl || images.renderUrl;
  const resolvedPhoto = withPhotoCache(
    isPlaceholderPhoto(photoUrl) ? images.photoUrl : photoUrl || images.photoUrl,
  );

  const drawingViews: ViewName[] = ['top', 'section', 'iso'];
  const viewLabel: Record<ViewName, string> = {
    photo: 'Bild',
    top: 'Von oben',
    section: 'Im Dach',
    iso: 'Schräg',
  };
  const viewTitle: Record<ViewName, string> = {
    photo: 'Produktansicht des Fensters',
    top: 'Draufsicht: Breite und Länge von oben',
    section:
      kind === 'access'
        ? 'Einbauschnitt: Scharnierseite oder Motorseite'
        : 'Seitenansicht: der Aufsatzkranz sitzt auf dem Dach',
    iso: 'Isometrie: Schrägansicht mit Maßen',
  };
  const viewCaption: Record<ViewName, string> = {
    photo: 'So sieht das Fenster aus.',
    top: showOpening
      ? 'Von oben: innen das Bestellmaß (Fenstermaß), außen die Rohbaulichte. 15 mm je Seite für die Deckenlaibung.'
      : 'Von oben: Breite und Länge — das Maß, das wir fertigen.',
    section:
      kind !== 'access'
        ? 'Der Aufsatzkranz liegt auf dem Dach. Die Rohbaulichte ist 30 mm größer.'
        : accessCut === 'hinge'
          ? 'Kurze Seite: Öffnung links, Scharnier rechts. Die Außenkante ist das Bestellmaß plus etwa 280 mm.'
          : 'Lange Seite: zwei Scherenmotoren im Aluminiumprofil. Links die Gesamthöhe.',
    iso: 'Schrägansicht: Isolierglas auf dem Aluminium-Aufsatzkranz.',
  };

  const compact = size !== 'lg';

  return (
    <div
      className={`sv-viewer ${size === 'lg' ? 'is-lg' : ''} ${view === 'section' ? 'is-section' : ''} ${view === 'photo' ? 'is-photo' : ''}`}
    >
      <div className="sv-viewer-stage" aria-live="polite">
        {view === 'photo' && (
          <PhotoView
            kind={kind}
            dimensions={dimensions}
            color={color}
            hideDimensions
            compact={compact}
            renderUrl={resolvedRender}
            photoUrl={resolvedPhoto}
          />
        )}
        {view === 'top' && (
          <TopView
            kind={kind}
            dimensions={dimensions}
            color={color}
            hideDimensions={hideDimensions}
            compact={compact}
            showOpening={showOpening && kind !== 'circular'}
          />
        )}
        {view === 'section' && kind === 'access' && (
          <AccessSectionView
            dimensions={dimensions}
            color={color}
            hideDimensions={hideDimensions}
            compact={compact}
            cut={accessCut}
          />
        )}
        {view === 'section' && kind !== 'access' && (
          <SectionView
            kind={kind}
            dimensions={dimensions}
            color={color}
            hideDimensions={hideDimensions}
            compact={compact}
            axis="length"
          />
        )}
        {view === 'iso' && (
          <IsoView
            kind={kind}
            dimensions={dimensions}
            color={color}
            hideDimensions={hideDimensions}
            compact={compact}
          />
        )}
      </div>

      {size === 'lg' && (
        <p className="sv-viewer-caption">
          {viewCaption[view]}
        </p>
      )}

      {showToggle && kind === 'access' && view === 'section' && (
        <div
          role="tablist"
          aria-label="Schnittrichtung"
          className={`sv-view-toggle ${size === 'sm' ? 'is-sm' : ''}`}
        >
          <button
            type="button"
            role="tab"
            aria-selected={accessCut === 'hinge'}
            title="Schnitt über die kurze Seite, mit Scharnier"
            onClick={(e) => {
              e.stopPropagation();
              setAccessCut('hinge');
            }}
            className={`sv-view-toggle-btn ${accessCut === 'hinge' ? 'is-active' : ''}`}
          >
            Scharnier
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={accessCut === 'motor'}
            title="Schnitt über die lange Seite, mit Scherenmotoren"
            onClick={(e) => {
              e.stopPropagation();
              setAccessCut('motor');
            }}
            className={`sv-view-toggle-btn ${accessCut === 'motor' ? 'is-active' : ''}`}
          >
            Motoren
          </button>
        </div>
      )}

      {showToggle && (
        <div
          role="tablist"
          aria-label="Ansicht wechseln"
          className={`sv-view-toggle ${size === 'sm' ? 'is-sm' : ''}`}
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === 'photo'}
            title={viewTitle.photo}
            onClick={(e) => {
              e.stopPropagation();
              setView('photo');
            }}
            className={`sv-view-toggle-btn ${view === 'photo' ? 'is-active' : ''}`}
          >
            {viewLabel.photo}
          </button>
          <span className="sv-view-toggle-split" aria-hidden />
          {drawingViews.map((v) => {
            const active = view === v;
            return (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={active}
                title={viewTitle[v]}
                onClick={(e) => {
                  e.stopPropagation();
                  setView(v);
                }}
                className={`sv-view-toggle-btn ${active ? 'is-active' : ''}`}
              >
                {viewLabel[v]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PhotoView({
  kind,
  dimensions,
  color,
  hideDimensions,
  compact = false,
  renderUrl,
  photoUrl,
}: {
  kind: ProductKind;
  dimensions: PerspectiveDimensions;
  color: string;
  hideDimensions: boolean;
  compact?: boolean;
  renderUrl?: string;
  photoUrl?: string;
}) {
  const sources = [photoUrl, renderUrl].filter((s): s is string => Boolean(s));
  const [srcIndex, setSrcIndex] = useState(0);
  const src = sources[srcIndex];

  const isCirc = kind === 'circular';
  const linear = kind === 'linear' ? linearSpan(dimensions) : null;
  const wMm = Math.max(
    300,
    isCirc
      ? dimensions.diameter ?? dimensions.width ?? 600
      : linear
        ? linear.bandMm
        : dimensions.width ?? 1200,
  );
  const lMm = Math.max(300, isCirc ? wMm : linear ? linear.shortMm : dimensions.length ?? 1200);

  /* Das Iso-PNG ist eine Produktaufnahme, keine maßstäbliche Zeichnung.
     object-fit: contain hält das Fenster vollständig sichtbar; die
     Bemaßung läuft um den Bildausschnitt, ohne das Foto umzurechnen. */
  const VB_W = compact ? 460 : 560;
  const VB_H = compact ? 248 : 420;
  const padX = compact ? 56 : 64;
  const padY = compact ? 36 : 44;
  const x = padX;
  const y = padY;
  const w = VB_W - padX - 36;
  const l = VB_H - padY - 28;

  if (!src) {
    return (
      <p className="text-[13px] text-ink-3 py-10 text-center">Kein Produktbild hinterlegt.</p>
    );
  }

  if (hideDimensions) {
    return (
      <div
        className="sv-photo-plate is-plain"
        style={{ aspectRatio: `${VB_W} / ${VB_H}` }}
      >
        <img
          src={src}
          alt={`SkyVision ${kind} — Produktansicht`}
          onError={() => {
            if (srcIndex < sources.length - 1) setSrcIndex((i) => i + 1);
          }}
        />
      </div>
    );
  }

  return (
    <div className="sv-photo-plate" style={{ aspectRatio: `${VB_W} / ${VB_H}` }}>
      <div
        className="sv-photo-plate-shot"
        style={{
          left: `${(x / VB_W) * 100}%`,
          top: `${(y / VB_H) * 100}%`,
          width: `${(w / VB_W) * 100}%`,
          height: `${(l / VB_H) * 100}%`,
        }}
      >
        <img
          src={src}
          alt=""
          onError={() => {
            if (srcIndex < sources.length - 1) setSrcIndex((i) => i + 1);
          }}
        />
      </div>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="sv-photo-plate-dims"
        role="img"
        aria-label={
          isCirc
            ? `Produktbild, Durchmesser ${fmt(wMm)} mm`
            : `Produktbild, Breite ${fmt(wMm)} mm, Länge ${fmt(lMm)} mm`
        }
      >
        {isCirc ? (
          <DimH
            x1={x}
            x2={x + w}
            y={y - 8}
            label={`Ø ${fmt(wMm)} mm`}
            color={color}
          />
        ) : (
          <>
            <DimH
              x1={x}
              x2={x + w}
              y={y - 8}
              label={`Breite ${fmt(wMm)} mm`}
              color={color}
            />
            <DimV
              y1={y}
              y2={y + l}
              x={x - 24}
              label={`Länge ${fmt(lMm)} mm`}
              color={color}
              rotateLabel
            />
          </>
        )}
      </svg>
    </div>
  );
}

/* ========================================================================
   Draufsicht — top view, scales with width × length.
   ======================================================================== */

interface ViewProps {
  kind: ProductKind;
  dimensions: PerspectiveDimensions;
  color: string;
  hideDimensions: boolean;
  compact?: boolean;
  showOpening?: boolean;
  axis?: 'width' | 'length';
}

/** Skaliert die tatsächliche Zeichnung so, dass die längere Kante `MAX_PX`
 *  belegt und die kürzere Kante proportional folgt. */
function fitRect(widthMm: number, lengthMm: number, maxPx = 220) {
  const ratio = widthMm / Math.max(1, lengthMm);
  if (ratio >= 1) {
    const w = maxPx;
    const l = maxPx / ratio;
    return { w, l };
  }
  const l = maxPx;
  const w = maxPx * ratio;
  return { w, l };
}

function TopView({ kind, dimensions, color, hideDimensions, compact = false, showOpening = false }: ViewProps) {
  const CX = compact ? 210 : 278;
  const CY = compact ? 152 : 214;
  const vb = compact ? '0 0 420 280' : '0 0 540 420';

  // ---- CIRCULAR ---------------------------------------------------------
  if (kind === 'circular') {
    const d = Math.max(300, dimensions.diameter ?? dimensions.width ?? 600);
    const r = compact ? 90 : 138;
    const inset = compact ? 5 : 7;

    return (
      <BaseSvg viewBox={vb}>
        <circle cx={CX} cy={CY} r={r} fill="none" stroke={color} strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
        <circle cx={CX} cy={CY} r={r - inset} fill="none" stroke={color} strokeWidth={0.7} vectorEffect="non-scaling-stroke" />
        {/* fadenkreuz */}
        <line x1={CX - r - 4} y1={CY} x2={CX + r + 4} y2={CY} stroke={color} strokeWidth={0.4} strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
        <line x1={CX} y1={CY - r - 4} x2={CX} y2={CY + r + 4} stroke={color} strokeWidth={0.4} strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
        {!hideDimensions && (
          <DimH x1={CX - r} x2={CX + r} y={CY + r + 22} label={`Durchmesser Ø ${fmt(d)} mm`} color={color} />
        )}
      </BaseSvg>
    );
  }

  // ---- LINEAR -----------------------------------------------------------
  if (kind === 'linear') {
    const { modules, opening, bandMm, shortMm, pitchMm } = linearSpan(dimensions);

    const { w, l } = fitRect(bandMm, shortMm, compact ? 280 : 360);
    const x = CX - w / 2;
    const y = CY - l / 2;
    const inset = 2.5;

    return (
      <BaseSvg viewBox={vb}>
        <rect x={x} y={y} width={w} height={l} fill="none" stroke={color} strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
        {Array.from({ length: modules }).map((_, i) => {
          const mx = x + (i * w) / modules;
          const mw = w / modules;
          const isOpening = i < opening;
          return (
            <g key={i}>
              {i > 0 && (
                <line x1={mx} y1={y} x2={mx} y2={y + l} stroke={color} strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
              )}
              <rect
                x={mx + inset}
                y={y + inset}
                width={mw - inset * 2}
                height={l - inset * 2}
                fill="none"
                stroke={color}
                strokeWidth={0.5}
                strokeDasharray={isOpening ? '3 2' : undefined}
                vectorEffect="non-scaling-stroke"
              />
              {isOpening && (
                <polyline
                  points={`${mx + mw * 0.28},${y + inset + 5} ${mx + mw * 0.5},${y + inset + 1} ${mx + mw * 0.72},${y + inset + 5}`}
                  fill="none"
                  stroke={color}
                  strokeWidth={0.8}
                  vectorEffect="non-scaling-stroke"
                />
              )}
              <text
                x={mx + mw / 2}
                y={y + l / 2 + 3}
                fill={MUTED}
                fontFamily="'Config Condensed', sans-serif"
                fontSize={9}
                textAnchor="middle"
              >{isOpening ? `A${i + 1}` : `M${i + 1}`}</text>
            </g>
          );
        })}
        {!hideDimensions && (
          <>
            <DimH x1={x} x2={x + w} y={y - 18} label={`${modules} Module × ${fmt(pitchMm)} mm`} color={color} />
            <DimV y1={y} y2={y + l} x={x - 18} label={`Länge ${fmt(shortMm)} mm`} color={color} rotateLabel />
          </>
        )}
      </BaseSvg>
    );
  }

  // ---- FIXED / COMFORT --------------------------------------------------
  const wMm = Math.max(300, dimensions.width ?? 1200);
  const lMm = Math.max(300, dimensions.length ?? 1200);
  const { w, l } = fitRect(wMm, lMm, compact ? (showOpening ? 168 : 186) : showOpening ? 268 : 312);
  const x = CX - w / 2;
  const y = CY - l / 2;
  const inset = compact ? 4 : 6;
  const gap = showOpening ? Math.min(compact ? 14 : 18, Math.max(9, Math.min(w, l) * 0.08)) : 0;

  return (
    <BaseSvg
      viewBox={vb}
      label={
        showOpening
          ? `Draufsicht: fertiges Fenster ${fmt(wMm)} mal ${fmt(lMm)} Millimeter, Rohbaulichte ${fmt(wMm + 30)} mal ${fmt(lMm + 30)} Millimeter.`
          : undefined
      }
    >
      {showOpening && (
        <rect
          x={x - gap}
          y={y - gap}
          width={w + gap * 2}
          height={l + gap * 2}
          fill="rgba(119, 183, 43, 0.08)"
          stroke={RUB}
          strokeWidth={1.1}
          strokeDasharray="5 3.5"
          vectorEffect="non-scaling-stroke"
        />
      )}
      <rect x={x} y={y} width={w} height={l} fill="none" stroke={color} strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
      <rect x={x + inset} y={y + inset} width={w - inset * 2} height={l - inset * 2} fill="none" stroke={color} strokeWidth={0.7} vectorEffect="non-scaling-stroke" />
      {(kind === 'comfort' || kind === 'access') && (
        <>
          {/* Öffnungspfeil zeigt "elektrisch öffenbar" */}
          <line x1={x + inset + 8} y1={y + inset + 8} x2={x + w - inset - 8} y2={y + inset + 8} stroke={color} strokeWidth={0.7} vectorEffect="non-scaling-stroke" />
          <polyline
            points={`${x + w - inset - 18},${y + inset + 4} ${x + w - inset - 8},${y + inset + 8} ${x + w - inset - 18},${y + inset + 12}`}
            fill="none"
            stroke={color}
            strokeWidth={0.7}
            vectorEffect="non-scaling-stroke"
          />
          {/* Motor-Marker */}
          <rect x={x + w + 4} y={y + l / 2 - 6} width={12} height={12} fill="none" stroke={color} strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
          <text x={x + w + 10} y={y + l / 2 + 3} fill={color} fontFamily="'Config Condensed', sans-serif" fontSize={8} textAnchor="middle">M</text>
        </>
      )}
      {showOpening && !hideDimensions && (
        <text
          x={x + w + gap + 6}
          y={y - gap + 11}
          fill={RUB}
          fontFamily="'Config Condensed', sans-serif"
          fontSize={10}
        >
          Rohbaulichte +30
        </text>
      )}
      {!hideDimensions && (
        <>
          <DimH
            x1={x}
            x2={x + w}
            y={y - (showOpening ? gap + 16 : 18)}
            label={`Breite ${fmt(wMm)} mm`}
            color={color}
          />
          <DimV
            y1={y}
            y2={y + l}
            x={x - (showOpening ? gap + 16 : 18)}
            label={`Länge ${fmt(lMm)} mm`}
            color={color}
            rotateLabel
          />
        </>
      )}
    </BaseSvg>
  );
}

/* ========================================================================
   ACCESS — Einbau nach Hersteller-Querschnitt (Sv Ac, 2025).
   Scharnierseite = kurze Seite, Motorseite = lange Seite.
   Außenkante = Bestellmaß + 280 mm, Rohbaulichte = Bestellmaß + 30 mm.
   ======================================================================== */

const ACCESS_OUTER_EXTRA_MM = 280;
const ACCESS_ROUGH_EXTRA_MM = 30;
const ACCESS_PRINT_MM = 130;
const ACCESS_CHEEK_MM = 140;

function AccessSectionView({
  dimensions,
  color,
  hideDimensions,
  compact = false,
  cut,
}: {
  dimensions: PerspectiveDimensions;
  color: string;
  hideDimensions: boolean;
  compact?: boolean;
  cut: 'hinge' | 'motor';
}) {
  const uid = useId().replace(/:/g, '');
  const woolId = `access-wool-${uid}`;
  const roofId = `access-roof-${uid}`;

  const hinge = cut === 'hinge';
  const orderMm = Math.max(300, hinge ? dimensions.width ?? 1000 : dimensions.length ?? 1000);
  const hMm = Math.max(150, dimensions.height ?? 291);
  const outerMm = orderMm + ACCESS_OUTER_EXTRA_MM;
  const roughMm = orderMm + ACCESS_ROUGH_EXTRA_MM;

  const vbW = compact ? 520 : 680;
  const vbH = compact ? 280 : 420;
  const orderPx =
    (compact ? 148 : 220) +
    Math.min(1, Math.max(0, (orderMm - 1000) / 800)) * (compact ? 70 : 120);
  const pxPerMm = orderPx / orderMm;
  const cheek = Math.max(compact ? 22 : 32, ACCESS_CHEEK_MM * pxPerMm);
  const printPx = Math.min(cheek - 5, ACCESS_PRINT_MM * (cheek / ACCESS_CHEEK_MM));
  const gapPx = Math.max(compact ? 5 : 7, (ACCESS_ROUGH_EXTRA_MM / 2) * pxPerMm);

  const CX = vbW / 2;
  const outerL = CX - (orderPx + cheek * 2) / 2;
  const outerR = CX + (orderPx + cheek * 2) / 2;
  const orderL = outerL + cheek;
  const orderR = outerR - cheek;
  const roughL = orderL - gapPx;
  const roughR = orderR + gapPx;

  const glassTop = compact ? 36 : 72;
  const glassH = compact ? 12 : 16;
  const slope = hinge ? Math.max(compact ? 4 : 5, orderPx * 0.02) : 0;
  const yTL = glassTop + slope;
  const yTR = glassTop;
  const yAt = (x: number, y0: number, y1: number) => y0 + ((y1 - y0) * (x - outerL)) / Math.max(1, outerR - outerL);
  const glassBotLeft = yTL + glassH;

  const hT = Math.min(1, Math.max(0, (hMm - 200) / 550));
  const kerbH = (compact ? 72 : 128) + hT * (compact ? 22 : 46);
  const kerbTop = glassBotLeft + (compact ? 5 : 7);
  const kerbBot = kerbTop + kerbH;
  const roofH = compact ? 30 : 50;
  const roofTop = kerbBot;
  const sheetL = compact ? 10 : 16;
  const sheetR = vbW - (compact ? 10 : 16);
  const skin = compact ? 3 : 4;

  const showNotes = !hideDimensions && !compact;

  return (
    <BaseSvg
      viewBox={`0 0 ${vbW} ${vbH}`}
      label={
        hinge
          ? `Einbauschnitt Scharnierseite. Bestellmaß ${fmt(orderMm)} Millimeter, Rohbaulichte ${fmt(roughMm)} Millimeter, Außenkante ${fmt(outerMm)} Millimeter, Höhe ${fmt(hMm)} Millimeter.`
          : `Einbauschnitt Motorseite. Bestellmaß ${fmt(orderMm)} Millimeter, Rohbaulichte ${fmt(roughMm)} Millimeter, Außenkante ${fmt(outerMm)} Millimeter, Höhe ${fmt(hMm)} Millimeter.`
      }
    >
      <defs>
        <pattern id={woolId} width="9" height="9" patternUnits="userSpaceOnUse" patternTransform="rotate(-18)">
          <path d="M0 2.5 Q4.5 0 9 2.5" fill="none" stroke={MUTED} strokeWidth={0.7} strokeOpacity={0.55} />
          <path d="M0 6.5 Q4.5 4.2 9 6.5" fill="none" stroke={MUTED} strokeWidth={0.55} strokeOpacity={0.4} />
        </pattern>
        <pattern id={roofId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke={MUTED} strokeOpacity={0.45} strokeWidth={0.8} />
        </pattern>
      </defs>

      <rect x={orderL} y={kerbTop} width={orderR - orderL} height={roofTop - kerbTop} fill="rgba(119, 183, 43, 0.07)" />
      <rect x={roughL} y={roofTop} width={roughR - roughL} height={(compact ? 22 : 34)} fill="rgba(119, 183, 43, 0.07)" />

      <RoofDeck x={sheetL} y={roofTop} w={roughL - sheetL} h={roofH} hatchId={roofId} color={color} label={showNotes ? 'Holz oder Beton' : undefined} />
      <RoofDeck x={roughR} y={roofTop} w={sheetR - roughR} h={roofH} hatchId={roofId} color={color} />

      <AccessCheek side="left" x={outerL} w={cheek} top={kerbTop} bot={kerbBot} skin={skin} woolId={woolId} color={color} />
      <AccessCheek side="right" x={orderR} w={cheek} top={kerbTop} bot={kerbBot} skin={skin} woolId={woolId} color={color} />

      <AccessSash
        outerL={outerL}
        outerR={outerR}
        yAt={yAt}
        yTL={yTL}
        yTR={yTR}
        glassH={glassH}
        printPx={printPx}
        color={color}
      />

      {hinge ? (
        <AccessHinge x={outerR - 1} y={yTR + 2} drop={kerbTop - (yTR + 2)} color={color} />
      ) : (
        <>
          <ScissorMotor x={orderL + skin} y={kerbTop + (compact ? 18 : 28)} w={compact ? 16 : 22} h={kerbH * 0.62} side="left" color={color} />
          <ScissorMotor x={orderR - skin - (compact ? 16 : 22)} y={kerbTop + (compact ? 18 : 28)} w={compact ? 16 : 22} h={kerbH * 0.62} side="right" color={color} />
        </>
      )}

      {showNotes && hinge && (
        <>
          <text x={CX} y={42} fill={MUTED} fontFamily="'Config Condensed', sans-serif" fontSize={12} textAnchor="middle">
            2 % Neigung
          </text>
          <text x={outerL} y={glassTop - 8} fill={MUTED} fontFamily="'Config Condensed', sans-serif" fontSize={12}>
            Öffnungsseite
          </text>
          <text x={outerR} y={glassTop - 8} fill={MUTED} fontFamily="'Config Condensed', sans-serif" fontSize={12} textAnchor="end">
            Scharnier
          </text>
        </>
      )}
      {showNotes && !hinge && orderPx > 140 && (
        <text x={CX} y={kerbBot - 10} fill={MUTED} fontFamily="'Config Condensed', sans-serif" fontSize={12} textAnchor="middle">
          Scherenmotor
        </text>
      )}

      {!hideDimensions && (
        <>
          {!compact && (
            <DimH x1={outerL} x2={outerR} y={22} label={`Außenkante ${fmt(outerMm)} mm`} color={color} />
          )}
          <DimH
            x1={orderL}
            x2={orderR}
            y={kerbTop + (compact ? 12 : 16)}
            label={compact ? `${fmt(orderMm)} mm` : `Bestellmaß ${fmt(orderMm)} mm`}
            color={color}
          />
          <DimH
            x1={roughL}
            x2={roughR}
            y={roofTop + roofH + (compact ? 14 : 18)}
            label={compact ? `Rohbau ${fmt(roughMm)}` : `Rohbaulichte ${fmt(roughMm)} mm`}
            color={color}
            below
          />
          {!compact && (
            <DimV
              x={Math.max(28, outerL - 26)}
              y1={yTR}
              y2={kerbBot}
              label={`Höhe ${fmt(hMm)} mm`}
              color={color}
              rotateLabel
            />
          )}
        </>
      )}
    </BaseSvg>
  );
}

function AccessCheek({
  side,
  x,
  w,
  top,
  bot,
  skin,
  woolId,
  color,
}: {
  side: 'left' | 'right';
  x: number;
  w: number;
  top: number;
  bot: number;
  skin: number;
  woolId: string;
  color: string;
}) {
  const h = bot - top;
  const woolX = x + skin;
  const woolW = Math.max(0, w - skin * 2);
  const outerSkinX = side === 'left' ? x : x + w - skin;
  return (
    <g>
      <rect x={x} y={top} width={w} height={h} fill="#FFFFFF" stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <rect x={woolX} y={top + skin} width={woolW} height={Math.max(0, h - skin * 2)} fill={PAPER2} />
      <rect x={woolX} y={top + skin} width={woolW} height={Math.max(0, h - skin * 2)} fill={`url(#${woolId})`} />
      <rect x={outerSkinX} y={top} width={skin} height={h} fill={INK} />
      <rect x={x} y={top} width={w} height={skin} fill={INK} />
      <rect x={x} y={bot - skin} width={w} height={skin} fill={INK} />
      <line
        x1={side === 'left' ? x + w : x}
        y1={top + skin}
        x2={side === 'left' ? x + w : x}
        y2={bot - skin}
        stroke={color}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}

function AccessSash({
  outerL,
  outerR,
  yAt,
  yTL,
  yTR,
  glassH,
  printPx,
  color,
}: {
  outerL: number;
  outerR: number;
  yAt: (x: number, y0: number, y1: number) => number;
  yTL: number;
  yTR: number;
  glassH: number;
  printPx: number;
  color: string;
}) {
  const top = (x: number) => yAt(x, yTL, yTR);
  const bot = (x: number) => top(x) + glassH;
  const quad = (x0: number, x1: number) =>
    `${x0},${top(x0)} ${x1},${top(x1)} ${x1},${bot(x1)} ${x0},${bot(x0)}`;
  const clearL = outerL + printPx;
  const clearR = outerR - printPx;
  const band = (frac: number) => {
    const y0 = yTL + glassH * frac;
    const y1 = yTR + glassH * frac;
    return { x1: clearL, y1: yAt(clearL, y0, y1), x2: clearR, y2: yAt(clearR, y0, y1) };
  };
  const upper = band(0.34);
  const lower = band(0.66);
  return (
    <g>
      <polygon points={quad(outerL, outerR)} fill={GLASS} />
      <line x1={upper.x1} y1={upper.y1} x2={upper.x2} y2={upper.y2} stroke={INK} strokeOpacity={0.28} strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
      <line x1={lower.x1} y1={lower.y1} x2={lower.x2} y2={lower.y2} stroke={INK} strokeOpacity={0.28} strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
      <polygon points={quad(outerL, clearL)} fill={INK} />
      <polygon points={quad(clearR, outerR)} fill={INK} />
      <polygon points={quad(outerL, outerR)} fill="none" stroke={color} strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
    </g>
  );
}

function AccessHinge({ x, y, drop, color }: { x: number; y: number; drop: number; color: string }) {
  return (
    <g stroke={color} fill={color}>
      <line x1={x - 14} y1={y + 1} x2={x} y2={y + 1} strokeWidth={1.6} vectorEffect="non-scaling-stroke" />
      <line x1={x} y1={y + 1} x2={x} y2={y + drop} strokeWidth={1.6} vectorEffect="non-scaling-stroke" />
      <circle cx={x} cy={y + 1} r={3.4} fill="#FFFFFF" stroke={color} strokeWidth={1.3} vectorEffect="non-scaling-stroke" />
      <circle cx={x} cy={y + 1} r={1.15} fill={color} stroke="none" />
    </g>
  );
}

function ScissorMotor({
  x,
  y,
  w,
  h,
  side,
  color,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  side: 'left' | 'right';
  color: string;
}) {
  const dir = side === 'left' ? 1 : -1;
  const bodyW = Math.max(8, w * 0.55);
  const bodyX = side === 'left' ? x : x + w - bodyW;
  const mountX = side === 'left' ? bodyX + bodyW : bodyX;
  const tipX = mountX + dir * w * 1.35;
  const topY = y + h * 0.12;
  const midY = y + h * 0.5;
  const lowY = y + h * 0.88;
  return (
    <g stroke={color} fill="none">
      <rect x={bodyX} y={y} width={bodyW} height={h} fill="#FFFFFF" stroke={color} strokeWidth={1.15} vectorEffect="non-scaling-stroke" />
      <line x1={bodyX + bodyW * 0.35} y1={y + 5} x2={bodyX + bodyW * 0.35} y2={y + h - 5} strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
      <line x1={bodyX + bodyW * 0.65} y1={y + 5} x2={bodyX + bodyW * 0.65} y2={y + h - 5} strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
      <line x1={mountX} y1={topY} x2={tipX} y2={midY} strokeWidth={1.15} vectorEffect="non-scaling-stroke" />
      <line x1={mountX} y1={lowY} x2={tipX} y2={midY} strokeWidth={1.15} vectorEffect="non-scaling-stroke" />
      <circle cx={tipX} cy={midY} r={1.8} fill={color} stroke="none" />
    </g>
  );
}

/* ========================================================================
   Einbau — Querschnitt wie der Hersteller-Schnitt:
   Isolierglas auf Aufsatzkranz, der auf dem Dach (Holz oder Beton) aufliegt.
   Die Rohbaulichte ist das Loch in der Decke; der Kranz sitzt darauf, nicht darin.
   ======================================================================== */

function SectionView({ kind, dimensions, color, hideDimensions, compact = false, axis = 'length' }: ViewProps) {
  const uid = useId().replace(/:/g, '');
  const woodId = `wood-${uid}`;
  const woolId = `wool-${uid}`;
  const roofId = `roof-${uid}`;
  const flashId = `flash-${uid}`;

  const alongLength = axis === 'length';
  const linear = kind === 'linear' ? linearSpan(dimensions) : null;
  const wMm = Math.max(
    300,
    kind === 'circular'
      ? dimensions.diameter ?? dimensions.width ?? 600
      : alongLength
        ? linear
          ? linear.shortMm
          : dimensions.length ?? 1200
        : linear
          ? linear.bandMm
          : dimensions.width ?? 1200,
  );
  const rohbauMm = wMm + 30;
  const hMm = Math.max(150, dimensions.height ?? 210);
  const modules = linear?.modules ?? Math.max(1, dimensions.modules ?? 3);

  const CX = compact ? 240 : 280;
  const sheetL = compact ? 8 : 12;
  const sheetR = compact ? 472 : 548;
  const minSpan = compact ? 132 : 220;
  const maxSpan = compact ? 220 : 360;
  const t = Math.min(1, Math.max(0, (wMm - 300) / 1500));
  const span = minSpan + t * (maxSpan - minSpan);
  /** Innenseite Aufsatzkranz = Fenstermaß / Bestellmaß. */
  const innerL = CX - span / 2;
  const innerR = CX + span / 2;

  const plasterW = compact ? 5 : 6;
  const woodInW = compact ? 13 : 16;
  const woolW = compact ? 24 : 30;
  const woodOutW = compact ? 14 : 17;
  const aluW = compact ? 5 : 6;
  const kerbW = plasterW + woodInW + woolW + woodOutW + aluW;

  /** 15 mm Luft je Seite, in Zeichen-px — Rohbaulichte ist 30 mm größer. */
  const gapPx = Math.max(compact ? 8 : 10, Math.min(compact ? 14 : 18, (span * 15) / Math.max(wMm, 1)));
  const roofInnerL = innerL - gapPx;
  const roofInnerR = innerR + gapPx;

  const kerbH = compact
    ? 52 + ((hMm - 210) / 210) * 14
    : 112 + ((hMm - 210) / 210) * 36;
  const glassH = kind === 'circular' ? (compact ? 26 : 44) : compact ? 16 : 30;
  const coverH = compact ? 3 : 4;
  const glassTop = compact ? 30 : 38;
  const glassBot = glassTop + glassH;
  const coverTop = glassBot;
  const coverBot = coverTop + coverH;
  const kerbTop = coverBot;
  const kerbBot = kerbTop + kerbH;
  const roofH = compact ? 28 : 52;
  const roofTop = kerbBot;
  const roofBot = roofTop + roofH;
  const roomBot = roofBot + (compact ? 22 : 38);

  const coverL = innerL - kerbW;
  const coverR = innerR + kerbW;
  /** Isolierglas liegt auf dem Kranz, nicht in einer Zarge. */
  const glassL = coverL;
  const glassR = coverR;

  return (
    <BaseSvg
      viewBox={compact ? '0 0 480 280' : '0 0 560 380'}
      label="Seitenansicht: Isolierglas liegt auf dem Aufsatzkranz, der Aufsatzkranz liegt auf dem Dach."
    >
      <WoodHatchPattern id={woodId} tone="ink" />
      <defs>
        <pattern
          id={woolId}
          width="9"
          height="9"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(-18)"
        >
          <path
            d="M0 2.5 Q4.5 0 9 2.5"
            fill="none"
            stroke={MUTED}
            strokeWidth={0.7}
            strokeOpacity={0.55}
          />
          <path
            d="M0 6.5 Q4.5 4.2 9 6.5"
            fill="none"
            stroke={MUTED}
            strokeWidth={0.55}
            strokeOpacity={0.4}
          />
        </pattern>
        <pattern
          id={roofId}
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="6" stroke={MUTED} strokeOpacity={0.45} strokeWidth={0.8} />
        </pattern>
        <pattern
          id={flashId}
          width="4"
          height="4"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(-45)"
        >
          <line x1="0" y1="0" x2="0" y2="4" stroke={INK} strokeOpacity={0.55} strokeWidth={0.7} />
        </pattern>
      </defs>

      {/* Lichtöffnung: zwischen den Kränzen und durch das Dachloch. */}
      <rect
        x={innerL}
        y={kerbTop}
        width={innerR - innerL}
        height={roofTop - kerbTop}
        fill="rgba(119, 183, 43, 0.07)"
      />
      <rect
        x={roofInnerL}
        y={roofTop}
        width={roofInnerR - roofInnerL}
        height={roomBot - roofTop}
        fill="rgba(119, 183, 43, 0.07)"
      />

      <RoofDeck
        x={sheetL}
        y={roofTop}
        w={roofInnerL - sheetL}
        h={roofH}
        hatchId={roofId}
        color={color}
        label={compact ? undefined : 'Holz oder Beton'}
      />
      <RoofDeck
        x={roofInnerR}
        y={roofTop}
        w={sheetR - roofInnerR}
        h={roofH}
        hatchId={roofId}
        color={color}
        label={compact ? undefined : 'Holz oder Beton'}
      />

      <KerbCut
        side="left"
        openX={innerL}
        top={kerbTop}
        bot={kerbBot}
        plasterW={plasterW}
        woodInW={woodInW}
        woolW={woolW}
        woodOutW={woodOutW}
        aluW={aluW}
        woodId={woodId}
        woolId={woolId}
        color={color}
      />
      <KerbCut
        side="right"
        openX={innerR}
        top={kerbTop}
        bot={kerbBot}
        plasterW={plasterW}
        woodInW={woodInW}
        woolW={woolW}
        woodOutW={woodOutW}
        aluW={aluW}
        woodId={woodId}
        woolId={woolId}
        color={color}
      />

      <Flashing
        side="left"
        outerX={coverL}
        roofY={roofTop}
        upY={kerbTop + kerbH * 0.38}
        hatchId={flashId}
        color={color}
      />
      <Flashing
        side="right"
        outerX={coverR}
        roofY={roofTop}
        upY={kerbTop + kerbH * 0.38}
        hatchId={flashId}
        color={color}
      />

      <rect
        x={coverL}
        y={coverTop}
        width={coverR - coverL}
        height={coverH}
        fill={INK}
        stroke={color}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />

      {kind === 'circular' ? (
        <path
          d={`M ${glassL} ${glassBot} Q ${CX} ${glassTop - 18} ${glassR} ${glassBot} Z`}
          fill={GLASS}
          stroke={color}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      ) : (
        <g transform={kind === 'access' ? `rotate(-18 ${glassL} ${glassBot})` : kind === 'comfort' ? `rotate(-6 ${glassL} ${glassBot})` : undefined}>
          <rect
            x={glassL}
            y={glassTop}
            width={glassR - glassL}
            height={glassH}
            fill={GLASS}
            stroke={color}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
          <line
            x1={glassL + 3}
            y1={glassTop + 6}
            x2={glassR - 3}
            y2={glassTop + 6}
            stroke={INK}
            strokeOpacity={0.22}
            strokeWidth={0.8}
            vectorEffect="non-scaling-stroke"
          />
          <line
            x1={glassL + 3}
            y1={glassBot - 6}
            x2={glassR - 3}
            y2={glassBot - 6}
            stroke={INK}
            strokeOpacity={0.22}
            strokeWidth={0.8}
            vectorEffect="non-scaling-stroke"
          />
          {kind === 'linear' &&
            !alongLength &&
            Array.from({ length: modules - 1 }).map((_, i) => {
              const gx = glassL + ((i + 1) * (glassR - glassL)) / modules;
              return (
                <line
                  key={i}
                  x1={gx}
                  y1={glassTop}
                  x2={gx}
                  y2={glassBot}
                  stroke={INK}
                  strokeWidth={1.4}
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
        </g>
      )}

      {!hideDimensions && (
        <>
          <DimH
            x1={glassL}
            x2={glassR}
            y={kind === 'comfort' || kind === 'access' || kind === 'circular' ? 20 : compact ? 22 : 32}
            label={`${kind === 'circular' ? 'Ø ' : ''}${fmt(wMm)} mm Fenstermaß`}
            color={color}
          />
          <DimH
            x1={roofInnerL}
            x2={roofInnerR}
            y={roomBot + 16}
            label={`${kind === 'circular' ? 'Ø ' : ''}${fmt(rohbauMm)} mm Rohbaulichte`}
            color={color}
            below
          />
          {!compact && (
            <DimV
              x={Math.min(coverR + 20, 536)}
              y1={kerbTop}
              y2={kerbBot}
              label={`${fmt(hMm)} mm`}
              color={color}
            />
          )}
        </>
      )}
    </BaseSvg>
  );
}

function RoofDeck({
  x,
  y,
  w,
  h,
  hatchId,
  color,
  label,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  hatchId: string;
  color: string;
  label?: string;
}) {
  if (w <= 0) return null;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill={ROOF}
        stroke={color}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <rect x={x} y={y} width={w} height={h} fill={`url(#${hatchId})`} />
      {label && w > 72 && (
        <text
          x={x + w / 2}
          y={y + h / 2 + 4}
          fill={INK}
          fontFamily="'Config Condensed', sans-serif"
          fontSize={11}
          textAnchor="middle"
        >
          {label}
        </text>
      )}
    </g>
  );
}

function Flashing({
  side,
  outerX,
  roofY,
  upY,
  hatchId,
  color,
}: {
  side: 'left' | 'right';
  outerX: number;
  roofY: number;
  upY: number;
  hatchId: string;
  color: string;
}) {
  const dir = side === 'left' ? -1 : 1;
  const spread = 22;
  const thick = 5;
  const x0 = outerX + dir * spread;
  const x1 = outerX;
  const d = [
    `M ${x0} ${roofY}`,
    `L ${x1} ${roofY}`,
    `L ${x1} ${upY}`,
    `L ${x1 + dir * thick} ${upY}`,
    `L ${x1 + dir * thick} ${roofY - thick}`,
    `L ${x0} ${roofY - thick}`,
    'Z',
  ].join(' ');
  return (
    <g>
      <path d={d} fill={PAPER2} stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <path d={d} fill={`url(#${hatchId})`} />
    </g>
  );
}

function KerbCut({
  side,
  openX,
  top,
  bot,
  plasterW,
  woodInW,
  woolW,
  woodOutW,
  aluW,
  woodId,
  woolId,
  color,
}: {
  side: 'left' | 'right';
  openX: number;
  top: number;
  bot: number;
  plasterW: number;
  woodInW: number;
  woolW: number;
  woodOutW: number;
  aluW: number;
  woodId: string;
  woolId: string;
  color: string;
}) {
  const dir = side === 'left' ? -1 : 1;
  const h = bot - top;
  const band = (from: number, width: number) => {
    const a = openX + dir * from;
    const b = openX + dir * (from + width);
    return { x: Math.min(a, b), w: Math.abs(b - a) };
  };
  const plaster = band(0, plasterW);
  const woodIn = band(plasterW, woodInW);
  const wool = band(plasterW + woodInW, woolW);
  const woodOut = band(plasterW + woodInW + woolW, woodOutW);
  const alu = band(plasterW + woodInW + woolW + woodOutW, aluW);

  return (
    <g>
      <rect x={plaster.x} y={top} width={plaster.w} height={h} fill="#FFFFFF" stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <rect x={woodIn.x} y={top} width={woodIn.w} height={h} fill={ROOF} stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <rect x={woodIn.x} y={top} width={woodIn.w} height={h} fill={`url(#${woodId})`} />
      <rect x={wool.x} y={top} width={wool.w} height={h} fill={PAPER2} stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <rect x={wool.x} y={top} width={wool.w} height={h} fill={`url(#${woolId})`} />
      <rect x={woodOut.x} y={top} width={woodOut.w} height={h} fill={ROOF} stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <rect x={woodOut.x} y={top} width={woodOut.w} height={h} fill={`url(#${woodId})`} />
      <rect x={alu.x} y={top} width={alu.w} height={h} fill={INK} stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
    </g>
  );
}

/* ========================================================================
   Iso — axonometric view, size scales with dimensions.
   ======================================================================== */

function IsoView({ kind, dimensions, color, hideDimensions, compact = false }: ViewProps) {
  const linear = kind === 'linear' ? linearSpan(dimensions) : null;
  const wMm =
    kind === 'circular'
      ? dimensions.diameter ?? dimensions.width ?? 600
      : linear
        ? linear.bandMm
        : dimensions.width ?? 1200;
  const lMm =
    kind === 'circular'
      ? dimensions.diameter ?? dimensions.length ?? 600
      : linear
        ? linear.shortMm
        : dimensions.length ?? 1200;

  const stageMax = compact ? 220 : 300;
  const scale = stageMax / Math.max(wMm, lMm);
  const w = wMm * scale;
  const l = lMm * scale;

  const CX = compact ? 210 : 270;
  const CY = compact ? 140 : 188;
  const kerbTh = compact ? 16 : 22;
  const glassTh = compact ? 7 : 10;
  const vb = compact ? '0 0 420 280' : '0 0 540 400';

  const iso = (x: number, y: number) => ({
    x: CX + (x - y) * 0.866,
    y: CY + (x + y) * 0.5,
  });
  const ring = (pts: { x: number; y: number }[]) =>
    pts.map((p) => `${p.x},${p.y}`).join(' ');
  const drop = (p: { x: number; y: number }, dy: number) => ({ x: p.x, y: p.y + dy });

  if (kind === 'circular') {
    const rx = (Math.min(w, l) / 2) * 0.866;
    const ry = (Math.min(w, l) / 2) * 0.5;
    const kerbTop = CY + glassTh;
    return (
      <BaseSvg viewBox={vb} label="Schrägansicht: Isolierglas liegt auf dem runden Aufsatzkranz.">
        <ellipse cx={CX} cy={kerbTop + kerbTh} rx={rx} ry={ry} fill={INK} stroke={INK} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <line x1={CX - rx} y1={kerbTop} x2={CX - rx} y2={kerbTop + kerbTh} stroke={INK} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <line x1={CX + rx} y1={kerbTop} x2={CX + rx} y2={kerbTop + kerbTh} stroke={INK} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <ellipse cx={CX} cy={kerbTop} rx={rx} ry={ry} fill={MUTED} stroke={INK} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <ellipse cx={CX} cy={CY + glassTh} rx={rx} ry={ry} fill={MUTED} stroke={INK} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <line x1={CX - rx} y1={CY} x2={CX - rx} y2={CY + glassTh} stroke={MUTED} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <line x1={CX + rx} y1={CY} x2={CX + rx} y2={CY + glassTh} stroke={MUTED} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <ellipse cx={CX} cy={CY} rx={rx} ry={ry} fill={GLASS} stroke={MUTED} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <text
          x={CX}
          y={CY + 4}
          textAnchor="middle"
          fill={INK}
          fontFamily="'Config Condensed', sans-serif"
          fontSize={13}
          fontWeight={500}
        >
          Glas
        </text>
        {!hideDimensions && (
          <IsoDimLabel x={CX} y={CY - ry - 12} label={`Durchmesser Ø ${fmt(wMm)} mm`} color={color} />
        )}
      </BaseSvg>
    );
  }

  const TL = iso(-w / 2, -l / 2);
  const TR = iso(w / 2, -l / 2);
  const BR = iso(w / 2, l / 2);
  const BL = iso(-w / 2, l / 2);
  const gTR = drop(TR, glassTh);
  const gBR = drop(BR, glassTh);
  const gBL = drop(BL, glassTh);
  const kTR = drop(TR, glassTh + kerbTh);
  const kBR = drop(BR, glassTh + kerbTh);
  const kBL = drop(BL, glassTh + kerbTh);
  const glassCx = (TL.x + TR.x + BR.x + BL.x) / 4;
  const glassCy = (TL.y + TR.y + BR.y + BL.y) / 4;

  return (
    <BaseSvg viewBox={vb} label="Schrägansicht: Isolierglas liegt auf dem Aluminium-Aufsatzkranz.">
      <polygon
        points={ring([gTR, gBR, kBR, kTR])}
        fill={MUTED}
        stroke={INK}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <polygon
        points={ring([gBL, gBR, kBR, kBL])}
        fill={INK}
        stroke={INK}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <polygon
        points={ring([TR, BR, gBR, gTR])}
        fill="#B7C9D6"
        stroke={MUTED}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <polygon
        points={ring([BL, BR, gBR, gBL])}
        fill="#9AAFBD"
        stroke={MUTED}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <polygon
        points={ring([TL, TR, BR, BL])}
        fill={GLASS}
        stroke={MUTED}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />

      {kind === 'linear' &&
        Array.from({ length: (linear?.modules ?? 3) - 1 }).map((_, i) => {
          const modules = linear?.modules ?? 3;
          const t = -w / 2 + (w / modules) * (i + 1);
          const A = iso(t, -l / 2);
          const B = iso(t, l / 2);
          return (
            <line
              key={i}
              x1={A.x}
              y1={A.y}
              x2={B.x}
              y2={B.y}
              stroke={INK}
              strokeOpacity={0.35}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}

      <text
        x={glassCx}
        y={glassCy + 4}
        textAnchor="middle"
        fill={INK}
        fontFamily="'Config Condensed', sans-serif"
        fontSize={13}
        fontWeight={500}
      >
        Glas
      </text>

      {!hideDimensions && (
        <>
          <IsoDimLabel x={(TL.x + TR.x) / 2} y={(TL.y + TR.y) / 2 - 14} label={`Breite ${fmt(wMm)} mm`} color={color} />
          <IsoDimLabel x={(TR.x + BR.x) / 2 + 14} y={(TR.y + BR.y) / 2} label={`Länge ${fmt(lMm)} mm`} color={color} anchor="start" />
        </>
      )}
    </BaseSvg>
  );
}

/* ========================================================================
   Primitives — kleine Bausteine, ohne DIN-Aufwand.
   ======================================================================== */

function BaseSvg({
  children,
  label,
  viewBox = '0 0 420 280',
}: {
  children: React.ReactNode;
  label?: string;
  viewBox?: string;
}) {
  return (
    <svg
      viewBox={viewBox}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      className="sv-viewer-svg"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {children}
    </svg>
  );
}

function DimH({
  x1,
  x2,
  y,
  label,
  color,
  below = false,
}: {
  x1: number;
  x2: number;
  y: number;
  label: string;
  color: string;
  below?: boolean;
}) {
  return (
    <g stroke={color} fill={color}>
      <line x1={x1} y1={y} x2={x2} y2={y} strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
      <polygon points={`${x1},${y - 3} ${x1 - 4},${y} ${x1},${y + 3}`} />
      <polygon points={`${x2},${y - 3} ${x2 + 4},${y} ${x2},${y + 3}`} />
      <text
        x={(x1 + x2) / 2}
        y={below ? y + 14 : y - 10}
        textAnchor="middle"
        fontFamily="'Config Condensed', sans-serif"
        fontSize={12}
        fill={color}
      >
        {label}
      </text>
    </g>
  );
}

function DimV({
  x,
  y1,
  y2,
  label,
  color,
  rotateLabel = false,
}: {
  x: number;
  y1: number;
  y2: number;
  label: string;
  color: string;
  rotateLabel?: boolean;
}) {
  const mid = (y1 + y2) / 2;
  const tx = rotateLabel ? x - 10 : x - 6;
  const ty = rotateLabel ? mid : mid + 3;
  return (
    <g stroke={color} fill={color}>
      <line x1={x} y1={y1} x2={x} y2={y2} strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
      <polygon points={`${x - 3},${y1} ${x},${y1 - 4} ${x + 3},${y1}`} />
      <polygon points={`${x - 3},${y2} ${x},${y2 + 4} ${x + 3},${y2}`} />
      <text
        x={tx}
        y={ty}
        textAnchor={rotateLabel ? 'middle' : 'end'}
        transform={rotateLabel ? `rotate(-90 ${tx} ${ty})` : undefined}
        fontFamily="'Config Condensed', sans-serif"
        fontSize={12}
        fill={color}
      >
        {label}
      </text>
    </g>
  );
}

function IsoDimLabel({
  x,
  y,
  label,
  color,
  anchor = 'middle',
}: {
  x: number;
  y: number;
  label: string;
  color: string;
  anchor?: 'start' | 'middle' | 'end';
}) {
  return (
    <text
      x={x}
      y={y}
      textAnchor={anchor}
      fontFamily="'Config Condensed', sans-serif"
      fontSize={12}
      fill={color}
    >
      {label}
    </text>
  );
}
